import { useState } from 'react';
import type { DraftState } from '../types';

interface Props {
  state: DraftState;
  onUndoLastPick: () => void;
  onUndoLastTrade: () => void;
  onVeto: () => void;
  onResetDraft: () => void;
  onArchiveAndReset: () => void;
  onSaveSnapshot: () => void;
  onOpenTrade: () => void;
  onUpdateBudget: (managerId: string, newBudget: number) => Promise<void>;
  onUpdateDraftYear?: (year: number) => Promise<void>;
}

export function CommissionerTools({
  state, onUndoLastPick, onUndoLastTrade, onVeto, onResetDraft,
  onArchiveAndReset, onSaveSnapshot, onOpenTrade, onUpdateBudget, onUpdateDraftYear,
}: Props) {
  const [collapsed, setCollapsed] = useState(true);
  const [editId, setEditId] = useState(state.managers[0]?.id ?? '');
  const [editBudget, setEditBudget] = useState(state.managers[0]?.initialBudget ?? 50);
  const [editYear, setEditYear] = useState(state.currentDraftYear);

  return (
    <div className="panel border-2 border-rose-700/80 overflow-hidden">
      <button
        onClick={() => setCollapsed(c => !c)}
        className="w-full p-3 bg-rose-700 hover:bg-rose-600 text-white font-extrabold flex justify-between items-center"
      >
        <span>🛡 Commissioner Tools</span>
        <span className={`transition-transform ${collapsed ? '' : 'rotate-90'}`}>▶</span>
      </button>
      {!collapsed && (
        <div className="p-4 space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <button
              onClick={onUndoLastPick}
              disabled={state.history.length === 0 || state.phase === 'Bidding'}
              className="btn-warn text-xs"
            >↩ Undo Pick</button>
            <button
              onClick={onVeto}
              disabled={state.phase !== 'Bidding'}
              className="btn-danger text-xs"
            >🚫 Veto Auction</button>
            <button onClick={onResetDraft} className="btn-ghost text-xs">⚠️ Factory Reset</button>
            <button
              onClick={onArchiveAndReset}
              disabled={!state.isDraftFinished}
              className="btn-primary text-xs"
            >📦 Archive & New Year</button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            <button
              onClick={onOpenTrade}
              disabled={state.phase === 'Bidding' || state.managers.every(m => m.teams.length === 0)}
              className="btn-primary text-xs"
            >🔄 Execute Trade</button>
            <button
              onClick={onUndoLastTrade}
              disabled={!state.trades || state.trades.length === 0}
              className="btn-warn text-xs"
            >↩ Undo Last Trade</button>
            <button
              onClick={onSaveSnapshot}
              disabled={state.history.length === 0}
              className="btn-ghost text-xs"
            >💾 Save Snapshot</button>
          </div>

          <div className="bg-slate-800/60 rounded-lg p-3">
            <h4 className="text-sm font-bold text-slate-200 mb-2">Edit Initial Budget</h4>
            <div className="flex gap-2">
              <select
                value={editId}
                onChange={e => {
                  setEditId(e.target.value);
                  const m = state.managers.find(m => m.id === e.target.value);
                  if (m) setEditBudget(m.initialBudget);
                }}
                className="flex-1 p-2 bg-slate-900 border border-slate-700 rounded text-white text-sm"
              >
                {state.managers.map(m => (
                  <option key={m.id} value={m.id}>{m.emoji} {m.name}</option>
                ))}
              </select>
              <input
                type="number"
                value={editBudget}
                min={0}
                onChange={e => setEditBudget(Math.max(0, Math.floor(Number(e.target.value) || 0)))}
                className="w-24 p-2 bg-slate-900 border border-slate-700 rounded text-white text-sm text-right font-bold"
              />
              <button
                onClick={() => onUpdateBudget(editId, editBudget)}
                className="btn-danger"
              >Update</button>
            </div>
          </div>

          {onUpdateDraftYear && (
            <div className="bg-slate-800/60 rounded-lg p-3">
              <h4 className="text-sm font-bold text-slate-200 mb-2">Edit Draft / Season Year</h4>
              <div className="flex gap-2 items-center">
                <input
                  type="number"
                  value={editYear}
                  min={2020}
                  max={2035}
                  onChange={e => setEditYear(Math.floor(Number(e.target.value) || state.currentDraftYear))}
                  className="w-32 p-2 bg-slate-900 border border-slate-700 rounded text-white text-sm font-bold text-center"
                />
                <button
                  onClick={() => onUpdateDraftYear(editYear)}
                  className="btn-primary text-xs"
                >
                  Set Season Year
                </button>
                <span className="text-xs text-slate-400">Current: {state.currentDraftYear}</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
